import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, CheckCircle2, Clock, XCircle, FileSearch } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUS_STYLE = {
  approved: "bg-teal-50 text-teal-700", disbursed: "bg-teal-50 text-teal-700",
  rejected: "bg-rose-50 text-rose-700",
  processing: "bg-amber-50 text-amber-700", submitted: "bg-sky-50 text-sky-700",
  clicked: "bg-slate-100 text-slate-600", form_started: "bg-slate-100 text-slate-600",
};

export default function LeadStatus() {
  const { t } = useLanguage();
  const [mobile, setMobile] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [perf, setPerf] = useState([]);

  const load = async (mob = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/my/leads${mob ? `?mobile=${mob}` : ""}`);
      setData(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); api.get("/my/source-performance").then((r) => setPerf(r.data.rows)).catch(() => {}); }, []);

  const search = (e) => { e?.preventDefault(); load(mobile); };

  return (
    <div className="space-y-6" data-testid="lead-status-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Lead Status</h1>
        <p className="text-slate-500 mt-1">{t("ls_subtitle")}</p>
      </div>

      <form onSubmit={search} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col sm:flex-row gap-3">
        <Input type="tel" maxLength={10} value={mobile} data-testid="my-leads-mobile"
          onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
          placeholder={t("ls_mobile_ph")} className="h-11 flex-1" />
        <div className="flex gap-2">
          <Button type="submit" disabled={loading} data-testid="my-leads-check-btn" className="bg-teal-600 hover:bg-teal-700 text-white font-bold h-11 px-6">
            <Search className="w-4 h-4 mr-1.5" /> {loading ? "Checking…" : "Check"}
          </Button>
          {mobile && <Button type="button" variant="outline" className="h-11" data-testid="my-leads-clear-btn" onClick={() => { setMobile(""); load(""); }}>Clear</Button>}
        </div>
      </form>

      {perf.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="source-performance">
          <h3 className="font-display font-bold text-[#0B223D]">Source-wise performance</h3>
          <p className="text-xs text-slate-400 mb-3">{t("ls_perf_note")}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100"><th className="p-2.5">Source</th><th className="p-2.5">Leads</th><th className="p-2.5">Conversions</th><th className="p-2.5">Conv %</th></tr></thead>
              <tbody>
                {perf.map((r) => (
                  <tr key={r.source} className="border-t border-slate-100" data-testid={`source-row-${r.source}`}>
                    <td className="p-2.5 font-semibold capitalize text-[#0B223D]">{r.source}</td>
                    <td className="p-2.5 font-mono-num">{r.leads}</td>
                    <td className="p-2.5 font-mono-num text-teal-700 font-bold">{r.conversions}</td>
                    <td className="p-2.5 font-mono-num text-indigo-600">{r.conv_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="my-leads-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-400 text-xs uppercase">
                <tr>
                  {["Lead ID", "Customer", "Mobile", "Campaign", "Status", "Date"].map((h) => <th key={h} className="text-left p-3.5 font-semibold whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.leads.map((l) => (
                  <tr key={l.lead_id} className="border-t border-slate-100" data-testid={`my-lead-${l.lead_id}`}>
                    <td className="p-3.5 font-mono-num text-xs">{l.lead_id}</td>
                    <td className="p-3.5 font-medium text-[#0B223D]">{l.full_name || "—"}</td>
                    <td className="p-3.5 font-mono-num text-xs text-slate-500">{l.mobile || "—"}</td>
                    <td className="p-3.5 text-slate-500">{l.campaign_name}</td>
                    <td className="p-3.5">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLE[l.status] || "bg-slate-100 text-slate-600"}`}>
                        {["approved", "disbursed"].includes(l.status) ? <CheckCircle2 className="w-3 h-3" /> : l.status === "rejected" ? <XCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                        {l.status_label}
                      </span>
                    </td>
                    <td className="p-3.5 text-xs text-slate-400 whitespace-nowrap">{l.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!data.leads.length && (
            <div className="p-10 text-center text-slate-400">
              <FileSearch className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">{t("ls_empty")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
